<template>
<v-container fluid grid-list-md style="width: 95%;">
    <!-- main heading -->
    <div class="d-flex flex-wrap">
        <h1>({{ hotelcode }}) {{ hotelname }} Daily Planner</h1>
        <ies-hotel-quick-links :pageid="hotelkey" />
    </div>
    
    <div>Rates are in {{ currencycode }} per room/unit per night{{ taxSummary }}</div>
    <v-layout row wrap>
            <v-flex xs12>

                <!-- table controls -->
                <ies-hotel-planner-table-controls v-if="responseDataReady"
                    :tableViewOptions="tableViewOptions"
                    :selectedTableViewOptions.sync="selectedTableViewOptions"
                    :hotelKey="hotelkey"
                    @updateNumMonths="handleUpdateVisibleMonths"
                    @updateAvailabilityRowDisplay="handleUpdateAvailabilityRowDisplay"
                    />
                                    
                <ies-hotel-planner-month-controls v-if="responseDataReady"
                    :hotelkey="hotelkey" 
                    :rateColumns="rateColumns"
                    :selectedDate.sync="selectedDate"
                    :isPlannerPage="true"
                    :resetMonthControls="resetMonthControls"
                    @update:resetMonthControls="handleMonthControlsReset"
                    />
                
                <!-- legend -->
                <div><strong>Legend</strong></div>
                <v-card class="mb-4">
                    <v-flex xs12 class="d-flex justify-space-between">
                        <div><span class="legend-icon room-available">&nbsp;</span> Available</div>
                        <div><span class="legend-icon room-on-request">&nbsp;</span> On Request</div>
                        <div><span class="legend-icon room-unavailable">&nbsp;</span> Unavailable</div>
                        <div><span class="legend-icon property-closed">&nbsp;</span> Property Closed</div>
                        <div><span class="legend-icon selected">&nbsp;</span> Selected for editing</div>
                        <div><span class="legend-icon updated">&nbsp;</span> Updated</div> 
                        <div class="indicative"><span class="legend-icon">&nbsp;</span> Indicative Rate</div>
                    </v-flex>                    
                </v-card>

                <!-- here is the table -->
                <ies-hotel-planner-table v-for="month in monthsToShow" v-if="responseDataReady && selectedDate"
                    :hotelkey="hotelkey"
                    :currencycode="currencycode"
                    :dateFrom="month"
                    :tableViewOptions="tableViewOptions"
                    :selectedTableViewOptions="selectedTableViewOptions"
                    :isDisabled="isChannelManaged || abrigedTableMode"
                    :abrigedTableMode="abrigedTableMode"
                    :availabilityCellDisplay="availabilityCellDisplay"
                    />                
            </v-flex>
        </v-layout>
        
    </v-container>
</template>

<script>
/**
* A Vue component dedicated to letting the user control availability and rates per
* hotel room for a given date range
*
* Props:  hotelkey
*/
import axios from 'axios';

export default {
    name: 'hotel-planner',
    props: ['hotelkey', 'hotelname', 'hotelcode', 'currencycode'],
    data () {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); 
        return {
            // Table data
            currentDate: currentDate,
            selectedDate: null,
            localResponseData: {},
            localTableData: [],
            resetMonthControls: false,

            visibleMonths: 1,
            availabilityCellDisplay: 'quantity',
        }
    },

    methods: {
        /**
         * @see data allFacilities
         */
        loadAllData(){
            axios.get('/api/hotel-planner', {'params': { 'hotelKey': this.hotelkey} } )
                .then(function(response){
                    this.localResponseData = response.data;
                    this.selectedDate = new Date(response.data.selectedDate);
                }
                .bind(this));
            },

        getDayOfWeek(index){
            switch(index){
                case 0: return 'Sun';
                case 1: return 'Mon';
                case 2: return 'Tue';
                case 3: return 'Wed';
                case 4: return 'Thur';
                case 5: return 'Fri';
                case 6: return 'Sat';
            }
            return '?';
        },

        getMonthOfYear(index){
            switch(index){
                case 0: return 'Jan';
                case 1: return 'Feb';
                case 2: return 'Mar';
                case 3: return 'Apr';
                case 4: return 'May';
                case 5: return 'Jun';
                case 6: return 'Jul';
                case 7: return 'Aug';
                case 8: return 'Sep';
                case 9: return 'Oct';
                case 10: return 'Nov';
                case 11: return 'Dec';
            }

            return '?';
        },

        handleUpdateVisibleMonths(newNum)
        {
            this.visibleMonths = newNum;
        },

        handleUpdateAvailabilityRowDisplay(newDisplay)
        {
            this.availabilityCellDisplay = newDisplay;
        },

        handleMonthControlsReset(){
            this.resetMonthControls = false;
        },
    },

    /**
     * Once mounted this component needs to load all available models
     */
    mounted() {
        this.loadAllData();

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    computed: {
        monthsToShow: function(){
            let months = [this.selectedDate];
            for(var monthIndex = 1; monthIndex < this.visibleMonths; monthIndex++){
                months.push(new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth() + monthIndex, 1));
            }

            return months;
        },

        abrigedTableMode: function(){
            return this.monthsToShow.length > 1;
        },

        tableViewOptions: function(){
            return this.localResponseData.tableViewOptions || {};
        },

        isChannelManaged: function(){
            return this.localResponseData.isChannelManaged ? true : false;
        },

        taxSummary : function(){
            return this.localResponseData.taxSummary || '';
        },

        hasUniversalRates: function(){
            return this.localTableData.universalRates ? true : false;
        },


        rateColumns : function(){
            let columns = [];

            if (!this.tableViewOptions.rateViewOptions) {
                return columns;
            }

            this.tableViewOptions.rateViewOptions.forEach(rateOption => {
                if (['rate_standard', 'rate_single'].includes(rateOption.field) || !this.hasUniversalRates){
                    columns.push(rateOption.field);
                } 
            });

            return columns;
        },

        selectedTableViewOptions: function(){
            return this.localResponseData.selectedTableViewOptions || {};
        },

        responseDataReady: function(){
          return Object.keys(this.localResponseData).length > 0;
        }
    },

    watch: 
    {
        visibleMonths: function(){
            if (this.visibleMonths == 1){
                this.availabilityCellDisplay = 'quantity';
            }
        }
    }
}
</script>

<style>

table.daily-planner {
    border-collapse: collapse;
    width: 100%;
}

table.daily-planner td {
    user-select: none;
}

table.daily-planner thead {
    position: sticky;
    top: -5px; 
    z-index: 1; 
}

table.daily-planner thead th {
    font-weight: bold;
    text-align: center;
    background-color: #f2f2f2;
}

table.daily-planner tbody tr td {
    text-align: center;
}

table.daily-planner td {
    border: 1px solid #c2c2c2;
    box-sizing: border-box;
}

table.daily-planner .header-cell {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    position: sticky;
    left: 0;
    z-index: 1;
    max-width: 200px;
    font-size: 14px;
}

.sticky-footer-header {
    border : 1px solid black;
    background-color: #d0d0d0;
}

.weekend {
    background-color: #c2c2c2 !important;
}

.interactive {
    cursor: pointer;
}

.interactive:hover {
    background-color: #CCCCFF;
}

.date-out-of-range {
    opacity: 0.5;
}

.disabled-cell {
  color: white; 
  cursor: not-allowed; 
  pointer-events: none;
  opacity: 0.5;
}

.disabled-rate-cell {
    background-color: black !important;
}

.room-available {
    background-color: #C1CD23;
}
.room-on-request {
    background-color: yellow;
}
.room-unavailable {
    background-color: #F15C61;
}

.property-closed {
    background-color: blue;
}

.availability-not-specified {
    background-color: darkslategray;
    color: white;
}

.rate-for-date, .availability-for-date {
    font-size: 0.75rem;
}
.updated {
    background-color: lightgreen !important;
}

.selected {
    background-color: #CCCCFF !important;
}

.indicative {
    color: purple;
    font-style: italic;
}


.date-text {
    font-size: 0.75rem;
    width: 100%;
    display: block;
}

.date-number {
    font-size: 1.25rem;
    width: 100%;
    display: block;
}

.roomName {
    background-color: orange;
}

.white-out {
    background-color: white;
    opacity: 0.4;
    width: 100%;
    height: 100%;
    position:fixed;
    left:0;
    top:0;
    z-index: 2;
}

.edit-form {
    background-color: white;
    position:fixed;
    left: calc(50vw - 255px);
    top: 30vh;
    z-index: 3;
    padding: 3rem;
    width: 510px;
    border: solid 1px black;
}

.legend-icon {
    width: 20px;
    height: 20px;
    display:inline-block;
}

.spin {
    animation: spin 1s infinite linear;
}

.table-container {
    padding: 0 !important;
    border: solid 1px black;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(-360deg);
  }
}

</style>
